import { Time } from "@internationalized/date";
import {
  add,
  addDays,
  addHours,
  addMinutes,
  differenceInCalendarDays,
  differenceInHours as differenceInHoursFn,
  differenceInMinutes,
  endOfDay as endOfDayFn,
  format as formatFns,
  isAfter,
  isBefore,
  isSameDay,
  isSameYear,
  isToday as isTodayFn,
  isValid,
  isYesterday as isYesterdayFn,
  parseISO,
  set,
  startOfDay as startOfDayFn,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import {
  format as formatInTimeZone,
  fromZonedTime,
  toDate,
  toZonedTime,
} from "date-fns-tz";

export const DEFAULT_TIME_ZONE = "America/New_York";
export const DATE_TIME_FORMAT = "yyyy-MM-dd HH:mm:ss";
export const TIME_FORMAT = "HH:mm:ss";
export const DISPLAY_DATETIME_FULL = "EEE, MMM d, yyyy, h:mm a";
export const DISPLAY_DATETIME_FULL_LONG = "EEEE, MMM d, yyyy, h:mm a";
export const DISPLAY_DATETIME_FULL_NO_YEAR = "EEE, MMM d, h:mm a";
export const DISPLAY_DATETIME_SHORT = "MMM d, yyyy, h:mm a";
export const DISPLAY_DATE = "MMMM d, yyyy";
export const DISPLAY_DATE_SHORT = "MMM d, yyyy";
export const DISPLAY_DATE_SHORT_NO_YEAR = "MMM d";
export const DISPLAY_TIME = "h:mm a";
export const DISPLAY_DATE_NO_YEAR = "EEE, MMM d";
export const DISPLAY_DATE_WITH_YEAR = "EEE, MMM d, yyyy";
export const DISPLAY_DATE_LONG = "P";
export const DISPLAY_TIME_LONG = "p";
export const ISO_DATE_FORMAT = "yyyy-MM-dd";
export const DISPLAY_WEEKDAY_MONTH_DAY = "EEEE, MMMM d";
export const DISPLAY_WEEKDAY_MONTH_DAY_YEAR = "EEEE, MMMM d, yyyy";
export const DISPLAY_DATETIME_SHORT_NO_YEAR = "MMM d, h:mm a";
export const DISPLAY_MONTH = "MMMM yyyy";
export const DISPLAY_MONTH_SHORT = "MMM yyyy";
export const DISPLAY_MONTH_SHORT_WITH_COMMA = "MMM, yyyy";
export const DISPLAY_MONTH_LONG = "MMMM, yyyy";

type Duration = "day" | "week" | "month" | "year";

/** IANA timezone from the browser (e.g. Records History “updated” line). Falls back to {@link DEFAULT_TIME_ZONE}. */
export function getBrowserTimeZone(): string {
  if (
    typeof Intl === "undefined" ||
    typeof Intl.DateTimeFormat === "undefined"
  ) {
    return DEFAULT_TIME_ZONE;
  }
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && tz.length > 0 ? tz : DEFAULT_TIME_ZONE;
  } catch {
    return DEFAULT_TIME_ZONE;
  }
}

/**
 * Formats an ISO string date into a readable format with optional timezone conversion.
 *
 * @param date - ISO date string or null
 * @param format - Format pattern (default: DISPLAY_DATETIME_FULL)
 * @param timeZone - Optional timezone for conversion
 * @returns Formatted date string or empty string if date is null
 *
 * @example
 * formatDate("2025-08-07T14:30:00.000Z")
 * // Returns: "Wed, Aug 7, 2025, 2:30 PM"
 *
 * @example
 * formatDate("2025-08-07T14:30:00.000Z", DISPLAY_DATE_SHORT, "America/New_York")
 * // Returns: "Aug 7, 2025"
 *
 * @example
 * formatDate(null)
 * // Returns: ""
 */
export const formatDate = (
  date: string | null,
  format = DISPLAY_DATETIME_FULL,
  timeZone?: string,
) => {
  if (!date) return "";
  const parsedDate = timeZone
    ? isoStringToZonedDate(date, timeZone)
    : parseISO(date);
  if (!parsedDate || !isValid(parsedDate)) return "";
  return formatFns(parsedDate, format);
};

/**
 * Formats an ISO string date as short date (e.g. "MMM d, yyyy").
 *
 * @param date - ISO date string or null/undefined
 * @param timeZone - Optional timezone for conversion
 * @returns Formatted date string or empty string if date is null/undefined
 */
export const formatShortDate = (
  date: string | null | undefined,
  timeZone?: string,
): string => {
  if (!date) return "";
  return formatDate(date, DISPLAY_DATE_SHORT, timeZone);
};

/**
 * Converts an ISO string to a Date object in the specified timezone.
 *
 * @param isoString - ISO date string
 * @param timeZone - Target timezone
 * @returns Date object converted to timezone or undefined if invalid input
 *
 * @example
 * isoStringToZonedDate("2025-08-07T14:30:00.000Z", "America/New_York")
 * // Returns: Date object representing 10:30 AM in New York
 *
 * @example
 * isoStringToZonedDate("", "America/New_York")
 * // Returns: undefined
 */
export const isoStringToZonedDate = (isoString: string, timeZone: string) => {
  if (!isoString || !timeZone) return undefined;
  return toZonedTime(isoString, timeZone);
};

/**
 * Gets the current Unix timestamp in seconds.
 *
 * @returns Current Unix timestamp as number
 *
 * @example
 * getUnixTimestamp()
 * // Returns: 1723043400 (current timestamp in seconds)
 */
export const getUnixTimestamp = () => {
  return Math.floor(Date.now() / 1000);
};

/**
 * Calculates duration between two dates/times and formats it as "Xh Ym".
 * Handles end-of-day times (23:59:59) by adding 1 minute.
 *
 * @param start - Start date/time (Date, string, or null)
 * @param end - End date/time (Date, string, or null)
 * @returns Formatted duration string or empty string if invalid input
 *
 * @example
 * getDuration("2025-08-07T09:00:00", "2025-08-07T11:30:00")
 * // Returns: "2h 30m"
 *
 * @example
 * getDuration("2025-08-07T09:00:00", "2025-08-07T10:00:00")
 * // Returns: "1h"
 *
 * @example
 * getDuration("2025-08-07T09:00:00", "2025-08-07T09:15:00")
 * // Returns: "15m"
 *
 * @example
 * getDuration(null, null)
 * // Returns: ""
 */
export const getDuration = (
  start?: Date | string | null,
  end?: Date | string | null,
): string => {
  if (!start || !end) return "";

  let endTime = end;

  // check if endTime is end of day (23:59:59)
  if (formatFns(endTime, "HH:mm:ss") === "23:59:59") {
    endTime = addMinutes(endTime, 1);
  }

  const hourDiff = differenceInHoursFn(endTime, start);
  const minDiff = differenceInMinutes(endTime, start) % 60;

  const hours = hourDiff > 0 ? `${hourDiff}h` : "";
  const minutes = minDiff > 0 ? `${minDiff}m` : "";
  const separator = hourDiff > 0 && minDiff > 0 ? " " : "";
  return `${hours}${separator}${minutes}`.trim();
};

/**
 * Formats time in short format, omitting minutes if they are zero.
 *
 * @param date - Date object to format
 * @param timeZone - Optional timezone for conversion
 * @returns Formatted time string ("9 AM" or "9:30 AM")
 *
 * @example
 * formatTimeShort(new Date("2025-08-07T09:00:00"))
 * // Returns: "9 AM"
 *
 * @example
 * formatTimeShort(new Date("2025-08-07T09:30:00"))
 * // Returns: "9:30 AM"
 *
 * @example
 * formatTimeShort(new Date("2025-08-07T14:00:00"), "America/New_York")
 * // Returns: "10 AM" (converted to NY timezone)
 */
export const formatTimeShort = (date: Date, timeZone?: string) => {
  let time = date;
  if (timeZone) {
    time = toZonedTime(date, timeZone);
  }

  const minutes = time.getMinutes();
  if (minutes === 0) {
    return formatFns(time, "h a");
  }
  return formatFns(time, "h:mm a");
};

/**
 * Gets the start of day for a given date in a specific timezone
 * @param date - The date to get start of day for
 * @param timeZone - The timezone to perform the calculation in. If not provided, uses local timezone
 * @returns A Date object representing the start of the day in the specified timezone
 */
export const startOfDay = (date: Date | string, timeZone?: string): Date => {
  const inputDate = typeof date === "string" ? new Date(date) : date;

  if (!timeZone) {
    return startOfDayFn(inputDate);
  }

  return startOfDayFn(toZonedTime(inputDate, timeZone));
};

/**
 * Gets the end of day for a given date in a specific timezone
 * @param date - The date to get end of day for
 * @param timeZone - The timezone to perform the calculation in. If not provided, uses local timezone
 * @returns A Date object representing the end of the day in the specified timezone
 */
export const endOfDay = (date: Date | string, timeZone?: string): Date => {
  const inputDate = typeof date === "string" ? new Date(date) : date;

  if (!timeZone) {
    return endOfDayFn(inputDate);
  }

  return endOfDayFn(toZonedTime(inputDate, timeZone));
};

/**
 * Gets the start of the week (Sunday) for a given date.
 *
 * @param date - Input date
 * @returns Date object representing start of week (Sunday)
 *
 * @example
 * getStartOfWeek(new Date("2025-08-07")) // Thursday
 * // Returns: Date object for Sunday, Aug 3, 2025
 *
 * @example
 * getStartOfWeek(new Date("2025-08-10")) // Sunday
 * // Returns: Date object for Sunday, Aug 10, 2025
 */
export const getStartOfWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = date.getDay();
  result.setDate(date.getDate() - day);
  return result;
};

/**
 * Gets the end of the week (Saturday) for a given date.
 *
 * @param date - Input date
 * @returns Date object representing end of week (Saturday)
 *
 * @example
 * getEndOfWeek(new Date("2025-08-07")) // Thursday
 * // Returns: Date object for Saturday, Aug 9, 2025
 *
 * @example
 * getEndOfWeek(new Date("2025-08-03")) // Sunday
 * // Returns: Date object for Saturday, Aug 9, 2025
 */
export const getEndOfWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = date.getDay();
  result.setDate(date.getDate() + (6 - day));
  return result;
};

/**
 * Converts a time string (HH:MM:SS) to a Date object with epoch date.
 *
 * @param timeString - Time in "HH:MM:SS" format
 * @returns Date object with January 1, 1970 as date
 *
 * @example
 * timeStringToDate("14:30:00")
 * // Returns: Date object representing Jan 1, 1970, 2:30 PM
 *
 * @example
 * timeStringToDate("09:15:30")
 * // Returns: Date object representing Jan 1, 1970, 9:15:30 AM
 */
export const timeStringToDate = (timeString: string): Date => {
  const [hours, minutes, seconds] = timeString.split(":").map(Number);
  return new Date(1970, 0, 1, hours, minutes, seconds);
};

export type DateTimePickerValue = {
  fromDateTime: string;
  toDateTime: string;
};

interface DisplayDateTimeWithSlot {
  date: string;
  dayPart: string;
  time: string;
  toString: () => string;
}

/**
 * Creates a structured display object for date/time ranges with day parts.
 * Automatically determines if the range is "All day" (12 hour duration) or categorizes by time of day.
 *
 * @param dateTimeRange - Object with fromDateTime and toDateTime ISO strings
 * @param timeZone - Optional timezone (defaults to DEFAULT_TIME_ZONE)
 * @param allowNight - When set, 12h span (9→21) is "All day"; when false, 9h (9→18). When omitted, both spans count as All day (message history).
 * @returns DisplayDateTimeWithSlot object with date, dayPart, time, and toString method
 *
 * @example
 * getDisplayDateTimeWithSlot({
 *   fromDateTime: "2025-08-07T09:00:00Z",
 *   toDateTime: "2025-08-07T11:00:00Z"
 * }, "America/New_York")
 * // Returns: {
 * //   date: "Thu, Aug 7",
 * //   dayPart: "Morning",
 * //   time: "5 AM -> 7 AM",
 * //   toString: () => "Thu, Aug 7, Morning (5 AM -> 7 AM)"
 * // }
 *
 * @example
 * getDisplayDateTimeWithSlot({
 *   fromDateTime: "2025-08-07T09:00:00Z",
 *   toDateTime: "2025-08-07T21:00:00Z"
 * })
 * // Returns: {
 * //   date: "Thu, Aug 7",
 * //   dayPart: "All day",
 * //   time: "5 AM -> 5 PM",
 * //   toString: () => "Thu, Aug 7, All day (5 AM -> 5 PM)"
 * // }
 */
export const getDisplayDateTimeWithSlot = (
  { fromDateTime, toDateTime }: DateTimePickerValue,
  timeZone?: string,
  allowNight?: boolean,
): DisplayDateTimeWithSlot => {
  if (!fromDateTime || !toDateTime) {
    return {
      date: "",
      dayPart: "",
      time: "",
      toString: () => "",
    };
  }

  const tz = timeZone || DEFAULT_TIME_ZONE;
  const zonedFrom = toZonedTime(parseISO(fromDateTime), tz);
  const zonedTo = toZonedTime(parseISO(toDateTime), tz);

  const spanHours = Math.round(
    differenceInHours(parseISO(toDateTime), parseISO(fromDateTime), tz),
  );

  const dateStr = formatFns(zonedFrom, DISPLAY_DATE_NO_YEAR);
  const timeStr = `${formatFns(zonedFrom, "h a")} -> ${formatFns(zonedTo, "h a")}`;

  const isAllDay =
    allowNight === true
      ? spanHours === 12
      : allowNight === false
        ? spanHours === 9
        : spanHours === 9 || spanHours === 12;

  const dayPart = isAllDay ? "All day" : getDayPart(zonedFrom.getHours());

  return {
    date: dateStr,
    dayPart,
    time: timeStr,
    toString: () => `${dateStr}, ${dayPart} (${timeStr})`,
  };
};

const getDayPart = (hour: number): string => {
  const dayParts = [
    { maxHour: 12, name: "Morning" },
    { maxHour: 15, name: "Midday" },
    { maxHour: 18, name: "Afternoon" },
    { maxHour: 24, name: "Evening" },
  ];

  return dayParts.find((part) => hour < part.maxHour)?.name || "Evening";
};

/**
 * Formats message timestamps with relative labels (Today, Yesterday).
 *
 * @param date - ISO date string
 * @returns Formatted timestamp string with relative labels
 *
 * @example
 * // Assuming today is Aug 7, 2025
 * formatMessageTimestamp("2025-08-07T14:30:00Z")
 * // Returns: "Today at 2:30 PM"
 *
 * @example
 * formatMessageTimestamp("2025-08-06T14:30:00Z")
 * // Returns: "Yesterday at 2:30 PM"
 *
 * @example
 * formatMessageTimestamp("2025-08-05T14:30:00Z")
 * // Returns: "Aug 5, 2025, 2:30 PM"
 *
 * @example
 * formatMessageTimestamp("")
 * // Returns: ""
 */
export const formatMessageTimestamp = (date: string) => {
  if (!date) return "";

  const parsedDate = parseISO(date);
  const timeStr = formatFns(parsedDate, DISPLAY_TIME);

  if (isTodayFn(parsedDate)) return `Today at ${timeStr}`;
  if (isYesterdayFn(parsedDate)) return `Yesterday at ${timeStr}`;

  return formatFns(parsedDate, DISPLAY_DATETIME_SHORT);
};

/**
 * Alternative message timestamp formatting with comma separator.
 * Uses "Today," format instead of "Today at" and omits year for past dates.
 *
 * @param date - ISO date string
 * @returns Formatted timestamp string with comma separator
 *
 * @example
 * // Assuming today is Aug 7, 2025
 * formatMessageTimestampVariant("2025-08-07T14:30:00Z")
 * // Returns: "Today, 2:30 PM"
 *
 * @example
 * formatMessageTimestampVariant("2025-08-05T14:30:00Z")
 * // Returns: "Aug 5, 2:30 PM"
 */
export const formatMessageTimestampVariant = (date: string) => {
  if (!date) return "";

  const parsedDate = parseISO(date);
  const timeStr = formatFns(parsedDate, DISPLAY_TIME);

  if (isTodayFn(parsedDate)) return `Today, ${timeStr}`;

  return formatFns(parsedDate, DISPLAY_DATETIME_SHORT_NO_YEAR);
};

/**
 * Formats duration in minutes to "Xh Ym" format.
 *
 * @param minutes - Duration in minutes
 * @returns Formatted duration string
 *
 * @example
 * formatDuration(150)
 * // Returns: "2h 30m"
 *
 * @example
 * formatDuration(60)
 * // Returns: "1h"
 *
 * @example
 * formatDuration(30)
 * // Returns: "30m"
 *
 * @example
 * formatDuration(90)
 * // Returns: "1h 30m"
 */
export const formatDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `${hours}h ${mins}m`;
  if (hours) return `${hours}h`;
  return `${mins}m`;
};

/**
 * Finds the earliest start and latest end times from multiple technician schedules.
 *
 * @param visitTechnicians - Array of date/time ranges for technicians
 * @returns Object with minDateTime and maxDateTime as ISO strings or null
 *
 * @example
 * getVisitDateTimeRange([
 *   { fromDateTime: "2025-08-07T09:00:00Z", toDateTime: "2025-08-07T11:00:00Z" },
 *   { fromDateTime: "2025-08-07T10:00:00Z", toDateTime: "2025-08-07T12:00:00Z" }
 * ])
 * // Returns: {
 * //   minDateTime: "2025-08-07T09:00:00.000Z",
 * //   maxDateTime: "2025-08-07T12:00:00.000Z"
 * // }
 *
 * @example
 * getVisitDateTimeRange([])
 * // Returns: { minDateTime: null, maxDateTime: null }
 */
export const getVisitDateTimeRange = (
  // Endpoints are optional on purpose: a crew row may legitimately carry no
  // booked window (`PATCH /visits/:visitId/technicians` stores NULL), and the
  // loop below already skips any entry missing either end. Declaring them
  // required only forced callers to cast the nullability away.
  visitTechnicians: {
    fromDateTime?: string | null;
    toDateTime?: string | null;
  }[],
) => {
  let minDateTime: Date | null = null;
  let maxDateTime: Date | null = null;

  visitTechnicians?.forEach((technician) => {
    if (!technician.fromDateTime || !technician.toDateTime) {
      return;
    }

    const technicianStart = parseISO(technician.fromDateTime);
    const technicianEnd = parseISO(technician.toDateTime);

    if (!minDateTime || isBefore(technicianStart, minDateTime)) {
      minDateTime = technicianStart;
    }

    if (!maxDateTime || isAfter(technicianEnd, maxDateTime)) {
      maxDateTime = technicianEnd;
    }
  });

  return {
    minDateTime: minDateTime ? (minDateTime as Date).toISOString() : null,
    maxDateTime: maxDateTime ? (maxDateTime as Date).toISOString() : null,
  };
};

/**
 * Creates a display string for arrival time windows.
 *
 * @param fromDateTime - Start time (ISO string or Date)
 * @param arrivalWindow - Window duration in hours
 * @param timeZone - Timezone (defaults to DEFAULT_TIME_ZONE)
 * @returns Formatted arrival window string
 *
 * @example
 * getDisplayArrivalWindow("2025-08-07T13:00:00Z", 2, "America/New_York")
 * // Returns: "Arriving between 9:00 AM - 11:00 AM"
 *
 * @example
 * getDisplayArrivalWindow(new Date("2025-08-07T16:00:00Z"), 1)
 * // Returns: "Arriving between 12:00 PM - 1:00 PM"
 */
export const getDisplayArrivalWindow = (
  fromDateTime: string | Date,
  arrivalWindow: number,
  timeZone: string = DEFAULT_TIME_ZONE,
): string => {
  const parsedFrom =
    typeof fromDateTime === "string" ? parseISO(fromDateTime) : fromDateTime;
  const parsedTo = addHours(parsedFrom, arrivalWindow);

  const zonedFrom = toZonedTime(parsedFrom, timeZone);
  const zonedTo = toZonedTime(parsedTo, timeZone);

  return `Arriving between ${formatFns(zonedFrom, DISPLAY_TIME)} - ${formatFns(zonedTo, DISPLAY_TIME)}`;
};

/**
 * Formats a time range with proper timezone handling and date formatting.
 * Handles end-of-day times (23:59:59) by rounding to start of next day.
 *
 * Returns `""` when either endpoint is absent or unparseable, rather than
 * throwing. Most callers are the order-activity renderers, which read
 * `newService.visit.fromDateTime` out of an append-only `Activity` JSONB
 * snapshot — a row written without a visit snapshot is a permanent state they
 * must render, not an exception. Absorbing that here keeps the check in one
 * place: `parseISO(undefined)` throws `Cannot read properties of undefined
 * (reading 'split')`, and one such row previously escaped to the top-level error
 * boundary and replaced an entire order page with "Something went wrong".
 *
 * @param fromDateTime - Start ISO string; absent/unparseable yields `""`
 * @param toDateTime - End ISO string; absent/unparseable yields `""`
 * @param timeZone - Timezone (defaults to DEFAULT_TIME_ZONE)
 * @returns Formatted time range string, or `""` when no range can be formed
 *
 * @example
 * getDisplayTimeRange(
 *   "2025-08-07T13:00:00Z",
 *   "2025-08-07T15:00:00Z",
 *   "America/New_York"
 * )
 * // Returns: "Wed, Aug 7, 9:00 AM - 11:00 AM"
 *
 * @example
 * getDisplayTimeRange(undefined, undefined)
 * // Returns: ""
 *
 * @example
 * getDisplayTimeRange(
 *   "2025-08-07T13:00:00Z",
 *   "2025-08-08T03:59:59Z",
 *   "America/New_York"
 * )
 * // Returns: "Wed, Aug 7, 9:00 AM - 12:00 AM" (rounded to next day)
 */
export const getDisplayTimeRange = (
  fromDateTime: string | null | undefined,
  toDateTime: string | null | undefined,
  timeZone: string = DEFAULT_TIME_ZONE,
): string => {
  // Total on missing input: activity payloads written without an embedded visit
  // (pre-parity V5 rows) reach this with undefined, and one bad row must render
  // as an empty range, not crash the page (parseISO(undefined) throws).
  if (!fromDateTime || !toDateTime) return "";

  const parsedFrom = parseISO(fromDateTime);
  const parsedTo = parseISO(toDateTime);

  if (!isValid(parsedFrom) || !isValid(parsedTo)) {
    return "";
  }

  const zonedFrom = toZonedTime(parsedFrom, timeZone);
  let zonedTo = toZonedTime(parsedTo, timeZone);

  // Round to start of next day if toDateTime is "23:59:59"
  const toTimeStr = formatFns(zonedTo, "HH:mm:ss");
  if (toTimeStr === "23:59:59") {
    zonedTo = startOfDayFn(addDays(zonedTo, 1));
  }

  const fromFormat = isSameYear(new Date(), zonedFrom)
    ? DISPLAY_DATETIME_FULL_NO_YEAR
    : DISPLAY_DATETIME_FULL;

  return `${formatFns(zonedFrom, fromFormat)} - ${formatFns(zonedTo, DISPLAY_TIME)}`;
};

/** A bare calendar day (`yyyy-MM-dd`), as opposed to an absolute instant. */
const ISO_DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Which calendar day an instant falls on, read in a given time zone.
 *
 * A day is a wall-clock fact, so it has to be read off the ZONED parts. Recovering it from
 * the instant's UTC date component — `iso.split("T")[0]` — disagrees with the zone whenever
 * the two differ: `2026-08-20T00:00+07:00` is `2026-08-19T17:00Z`, so everyone east of UTC
 * reads a day early, and a late-evening New York instant (8 PM EDT = `00:00Z`) reads a day
 * late.
 *
 * @param instantIso - An absolute instant (ISO 8601). `""` for "nothing picked yet".
 * @param timeZone - IANA zone the day is read in.
 * @returns `yyyy-MM-dd` in `timeZone`, or `""` when there is no instant to read.
 */
export function getZonedCalendarDay(
  instantIso: string,
  timeZone: string,
): string {
  // `formatDate` with a zone routes through `isoStringToZonedDate`, which is the
  // conversion that makes this read the zoned parts rather than the UTC ones. Note the
  // `formatInTimeZone` imported in this file is date-fns-tz's `format`, which does NOT
  // convert — its `timeZone` option only feeds the zone-NAME tokens (z/zzz/xxx).
  return formatDate(instantIso, ISO_DATE_FORMAT, timeZone);
}

/**
 * Combines a calendar day with a Time object, both read in `timeZone`, into a UTC ISO string.
 *
 * @param isoString - Either a bare `yyyy-MM-dd` day or an absolute instant. An instant's day
 *   is read in `timeZone` via {@link getZonedCalendarDay}, so callers never pre-normalize.
 *   Empty or unparseable input throws `RangeError` rather than yielding a bogus timestamp.
 * @param time - Time object with hour, minute, and optional second
 * @param timeZone - Zone both the day and the time are interpreted in
 * @returns UTC ISO string representation
 *
 * @example
 * combineDateAndTime("2025-06-04", new Time(9, 30), "America/New_York")
 * // Returns: "2025-06-04T13:30:00.000Z"
 *
 * @example
 * // An instant's day comes from the zone, never from UTC — this is Aug 27 in New York.
 * combineDateAndTime("2026-08-28T00:00:00.000Z", new Time(0, 0), "America/New_York")
 * // Returns: "2026-08-27T04:00:00.000Z"
 */
export function combineDateAndTime(
  isoString: string,
  time: Time,
  timeZone: string,
): string {
  const zonedDay = ISO_DATE_ONLY_PATTERN.test(isoString)
    ? isoString
    : getZonedCalendarDay(isoString, timeZone);
  const [year, month, day] = zonedDay.split("-").map(Number);

  const localDateTimeStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")} ${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")}:${String(time.second ?? 0).padStart(2, "0")}`;

  const utcDate = fromZonedTime(localDateTimeStr, timeZone);

  return utcDate?.toISOString();
}

/**
 * Extracts time components from an ISO string in a specific timezone.
 *
 * @param isoString - ISO date string
 * @param timeZone - Target timezone for extraction
 * @returns Time object with hour, minute, and second components
 *
 * @example
 * getTimeFromISOString("2025-08-07T13:30:45Z", "America/New_York")
 * // Returns: Time { hour: 9, minute: 30, second: 45 }
 *
 * @example
 * getTimeFromISOString("2025-08-07T20:15:00Z", "America/Los_Angeles")
 * // Returns: Time { hour: 13, minute: 15, second: 0 }
 */
export function getTimeFromISOString(
  isoString: string,
  timeZone: string,
): Time | null {
  if (!isoString) {
    return null;
  }
  const zonedDate = toZonedTime(isoString, timeZone);
  return new Time(
    zonedDate.getHours(),
    zonedDate.getMinutes(),
    zonedDate.getSeconds(),
  );
}
/**
 * Creates a visit name from datetime range, handling empty end times.
 *
 * @param fromDateTime - Start ISO string
 * @param toDateTime - End ISO string (can be empty)
 * @param timeZone - Timezone (defaults to DEFAULT_TIME_ZONE)
 * @returns Formatted visit name string
 *
 * @example
 * formatVisitNameByDatetime("2025-08-07T13:00:00Z", "2025-08-07T15:00:00Z", "America/New_York")
 * // Returns: "Aug 7, 2025, 1:00 PM - 3:00 PM"
 *
 * @example
 * formatVisitNameByDatetime("2025-08-07T13:00:00Z", "", "America/New_York")
 * // Returns: "Aug 7, 2025, 1:00 PM"
 */
export const formatVisitNameByDatetime = (
  fromDateTime: string,
  toDateTime: string,
  timeZone = DEFAULT_TIME_ZONE,
) => {
  if (toDateTime === "") {
    return formatDate(fromDateTime, DISPLAY_DATETIME_SHORT, timeZone);
  }
  const startDate = formatDate(fromDateTime, DISPLAY_DATETIME_SHORT, timeZone);
  return `${startDate} - ${formatDate(toDateTime, DISPLAY_TIME, timeZone)}`;
};

/**
 * Calculates the difference in hours between two dates in a specific timezone
 * @param dateLeft - The later date
 * @param dateRight - The earlier date
 * @param timeZone - The timezone to perform the calculation in (e.g., 'America/New_York'). If not provided, uses local timezone
 * @returns The number of hours difference (positive if dateLeft is after dateRight)
 */
export const differenceInHours = (
  dateLeft: Date | string,
  dateRight: Date | string,
  timeZone?: string,
): number => {
  const leftDate = typeof dateLeft === "string" ? new Date(dateLeft) : dateLeft;
  const rightDate =
    typeof dateRight === "string" ? new Date(dateRight) : dateRight;

  if (!timeZone) {
    // use local timezone calculation if no timezone is provided
    return differenceInHoursFn(leftDate, rightDate);
  }

  const leftInTimeZone = toZonedTime(leftDate, timeZone);
  const rightInTimeZone = toZonedTime(rightDate, timeZone);

  return differenceInHoursFn(leftInTimeZone, rightInTimeZone);
};

/**
 * Calculates the difference in hours between now and a given date in a specific timezone
 * @param date - The date to compare against now
 * @param timeZone - The timezone to perform the calculation in. If not provided, uses local timezone
 * @returns The number of hours difference (positive if now is after the date)
 */
export const hoursFromNow = (
  date: Date | string,
  timeZone?: string,
): number => {
  return differenceInHours(new Date(), date, timeZone);
};

/** Wall-clock threshold: under this span, show "In N hours" instead of calendar days. */
const MS_LESS_THAN_ONE_DAY = 24 * 60 * 60 * 1000;

/**
 * Calendar days from "today" to the visit instant in `timeZone` (0 = same local calendar day).
 * Negative if the visit's calendar day is before today in that zone.
 *
 * @param isoDate - ISO datetime string (e.g. visit `fromDateTime`)
 * @param timeZone - IANA zone for calendar boundaries; omitted uses the runtime local zone (via `startOfDay`)
 */
export const getDaysUntil = (
  isoDate: string,
  timeZone?: string,
): number | null => {
  if (!isoDate) return null;
  try {
    const visitInstant = parseISO(isoDate);
    const now = new Date();
    return differenceInCalendarDays(
      startOfDay(visitInstant, timeZone),
      startOfDay(now, timeZone),
    );
  } catch {
    return null;
  }
};

/**
 * Member visit card relative label: "In 2 hours", "In 2 days", "3 days ago", etc.
 * Uses `timeZone` for calendar-day boundaries and hour deltas (see `differenceInHours`).
 * Sub-hour segments use absolute instants (`differenceInMinutes`).
 */
export const formatVisitRelativeTime = (
  isoDate: string,
  timeZone?: string,
): string | null => {
  if (!isoDate) return null;

  let visitDate: Date;
  try {
    visitDate = parseISO(isoDate);
  } catch {
    return null;
  }

  const now = new Date();
  const diffMs = visitDate.getTime() - now.getTime();
  const daysUntil = getDaysUntil(isoDate, timeZone);

  if (diffMs > 0) {
    if (diffMs < MS_LESS_THAN_ONE_DAY) {
      const hours = differenceInHours(visitDate, now, timeZone);
      if (hours >= 1) {
        return `In ${hours} ${hours === 1 ? "hour" : "hours"}`;
      }
      const minutes = differenceInMinutes(visitDate, now);
      if (minutes < 1) return "Starting soon";
      return "In less than an hour";
    }
    if (daysUntil === null) return null;
    if (daysUntil <= 0) {
      const hours = differenceInHours(visitDate, now, timeZone);
      return `In ${hours} ${hours === 1 ? "hour" : "hours"}`;
    }
    const dayText = `${daysUntil} ${daysUntil === 1 ? "day" : "days"}`;
    return `In ${dayText}`;
  }

  if (daysUntil === null) return null;
  if (daysUntil < 0) {
    const daysAgo = Math.abs(daysUntil);
    return `${daysAgo} ${daysAgo === 1 ? "day" : "days"} ago`;
  }

  const hoursAgo = differenceInHours(now, visitDate, timeZone);
  if (hoursAgo < 1) {
    const minsAgo = differenceInMinutes(now, visitDate);
    if (minsAgo < 1) return "Just now";
    return `${minsAgo} ${minsAgo === 1 ? "minute" : "minutes"} ago`;
  }
  return `${hoursAgo} ${hoursAgo === 1 ? "hour" : "hours"} ago`;
};

/**
 * True if the visit's end (`toDateTime`) calendar-day is before "today" in
 * `timeZone` (or local when omitted) — the same upcoming/past boundary the
 * office timeline uses. A visit stays "active" through its whole end day even
 * after the end time passes, and turns "past" only once that day is over. A
 * missing `toDateTime` returns false (treated as not-yet-ended).
 */
export const hasVisitEndedBeforeToday = (
  toDateTime: string | null | undefined,
  timeZone?: string,
): boolean => {
  if (!toDateTime) return false;
  const tz = timeZone ?? DEFAULT_TIME_ZONE;
  return isBefore(toZonedTime(toDateTime, tz), startOfDay(new Date(), tz));
};

/**
 * Formats an arrival window time range (e.g. `"9:00 AM - 11:00 AM"`) in `timeZone`.
 *
 * @param fromDateTime - ISO start time
 * @param arrivalWindow - Duration in hours (defaults to 2 when null)
 * @param timeZone - IANA timezone for displaying times
 */
export const formatArrivalWindow = (
  fromDateTime: string,
  arrivalWindow: number | null,
  timeZone: string,
): string => {
  try {
    if (!fromDateTime || !timeZone) return "";
    const parsedFrom = parseISO(fromDateTime);
    const windowHours = arrivalWindow ?? 2;
    const parsedTo = addHours(parsedFrom, windowHours);
    const fromTime = formatDate(fromDateTime, DISPLAY_TIME, timeZone);
    const toTime = formatDate(parsedTo.toISOString(), DISPLAY_TIME, timeZone);
    return `${fromTime} - ${toTime}`;
  } catch {
    return "";
  }
};

/**
 * Gets a date range for a specific duration period in a given timezone
 * @param date - The reference date to calculate the range from
 * @param duration - The duration period ('day', 'week', 'month', or 'year')
 * @param timeZone - The timezone to perform the calculation in
 * @returns An object with fromDateTime and toDateTime as ISO strings in UTC
 * @example
 * // Get the full week range for a specific date
 * const range = getDateRange(new Date(), 'week', 'America/New_York');
 * // Returns: { fromDateTime: "2024-01-07T05:00:00.000Z", toDateTime: "2024-01-14T05:00:00.000Z" }
 */
export const getDateRange = (
  date: Date | string,
  duration: Duration,
  timeZone: string,
): { fromDateTime: string; toDateTime: string } => {
  const startFunctions = {
    day: startOfDay,
    week: startOfWeek,
    month: startOfMonth,
    year: startOfYear,
  };

  const inputDate = typeof date === "string" ? new Date(date) : date;
  const dateInTimezone = toZonedTime(inputDate, timeZone);

  const fromDateTime = startFunctions[duration](dateInTimezone);
  const toDateTime = add(fromDateTime, { [`${duration}s`]: 1 });

  return {
    fromDateTime: fromZonedTime(fromDateTime, timeZone).toISOString(),
    toDateTime: fromZonedTime(toDateTime, timeZone).toISOString(),
  };
};

/**
 * Mapping of common time zone abbreviations to their full names
 */
const abbrs: Record<string, string> = {
  EST: "Eastern Standard Time",
  EDT: "Eastern Daylight Time",
  CST: "Central Standard Time",
  CDT: "Central Daylight Time",
  MST: "Mountain Standard Time",
  MDT: "Mountain Daylight Time",
  PST: "Pacific Standard Time",
  PDT: "Pacific Daylight Time",
};

/**
 * Converts a time zone identifier to a structured display object
 *
 * @param timeZone - IANA time zone identifier (e.g., "America/New_York", "Europe/London")
 * @returns An object containing formatted time zone information for display purposes
 *
 * @example
 * ```typescript
 * const display = timeZoneToDisplay("America/New_York");
 * // Returns: {
 * //   id: "America/New_York",
 * //   displayName: "Eastern Standard Time - New York",
 * //   abbreviation: "EST",
 * //   utcOffset: "-05:00",
 * //   default: false
 * // }
 * ```
 */
export const timeZoneToDisplay = (timeZone: string) => {
  const tzOptions = {
    timeZone: timeZone,
    locale: enUS,
  };

  const now = new Date();

  const shortName = formatInTimeZone(now, "zzz", tzOptions);
  const longName = formatInTimeZone(now, "zzzz", tzOptions);
  const utcOffset = formatInTimeZone(now, "xxx", tzOptions);
  const city = timeZone.split("/").pop()?.replace(/_/g, " ") || timeZone;

  return {
    id: timeZone,
    displayName: `${longName} - ${city}`,
    abbreviation: shortName,
    utcOffset: `GMT${utcOffset}`,
    default: false,
  };
};

/**
 * Combines a date and a time in the specified timezone
 * @param date - A Date object from which to extract the date part
 * @param time - A Date object from which to extract the time part
 * @param timeZone - Target timezone (e.g. "America/New_York")
 * @returns A Date object representing the combined date and time in the specified timezone
 */
export const combineDateTime = (
  date: Date,
  time: Date,
  timeZone: string,
): Date => {
  const timeStr = formatFns(time, TIME_FORMAT);

  const dateInTargetTZ = toZonedTime(date, timeZone);
  const dateStr = formatFns(dateInTargetTZ, ISO_DATE_FORMAT);

  const dateTimeStr = `${dateStr}T${timeStr}`;
  return toDate(dateTimeStr, { timeZone });
};

/**
 * Gets a date/time representing local time in a given time zone from the UTC date
 */
export const utcToZonedTime = (date: string, timeZone: string): Date => {
  const dateStr = date.replace("Z", "");

  return toDate(dateStr, { timeZone });
};

export function formatDurationFromSeconds(seconds: number = 0): string {
  if (seconds <= 0) return "0m";

  const days = Math.floor(seconds / 86400); // 24 * 60 * 60
  const hours = Math.floor((seconds % 86400) / 3600); // 60 * 60
  const minutes = Math.floor((seconds % 3600) / 60);

  // Return only the highest unit
  if (days > 0) {
    return `${days}d`;
  }

  if (hours > 0) {
    return `${hours}h`;
  }

  return `${minutes}m`;
}

/**
 * Formats a date into an ISO 8601 date-time string
 *
 * @param year - The full year (e.g., 2024)
 * @param month - The month number (1-12, where 1 = January, 12 = December)
 * @param day - The day of the month (1-31), defaults to 1
 * @returns An ISO 8601 formatted date-time string (e.g., "2024-03-01T00:00:00")
 *
 * @example
 * formatISODate(2024, 3, 15) // Returns "2024-03-15T00:00:00"
 * formatISODate(2024, 1)     // Returns "2024-01-01T00:00:00"
 * formatISODate(2024, 12, 31) // Returns "2024-12-31T00:00:00"
 */
export const formatISODate = (
  year: number,
  month: number,
  day: number = 1,
): string => {
  // ensure month is always 2 digits (e.g., "03" instead of "3")
  const monthStr: string = String(month).padStart(2, "0");

  // ensure day is always 2 digits (e.g., "01" instead of "1")
  const dayStr: string = String(day).padStart(2, "0");

  // combine into ISO 8601 format: YYYY-MM-DDTHH:MM:SS
  return `${year}-${monthStr}-${dayStr}T00:00:00`;
};

export const setUTCTimeToTzMidnight = (
  isoString: string,
  timeZone: string,
): string => {
  const utcDate = new Date(isoString);
  const nyDate = toZonedTime(utcDate, timeZone);
  const nyMidnight = set(nyDate, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const backToUtc = fromZonedTime(nyMidnight, timeZone);
  return backToUtc.toISOString();
};

/**
 * Adds calendar days in a target time zone safely across DST
 * @param date - the original date
 * @param days - number of calendar days to add
 * @param timeZone - IANA time zone string, e.g., 'America/New_York'
 */
export const addDaysInTimeZone = (
  date: Date,
  days: number,
  timeZone: string,
): Date => {
  const zonedDate = toZonedTime(date, timeZone);

  const newZonedDate = addDays(zonedDate, days);

  const utcDate = fromZonedTime(newZonedDate, timeZone);

  return utcDate;
};

/**
 * Checks if a date is today in a specific timezone
 *
 * @param date - The date to check
 * @param timeZone - The timezone to perform the check in. If not provided, uses local timezone
 * @returns True if the date is today in the specified timezone, false otherwise
 */
export const isToday = (date: Date, timeZone: string): boolean => {
  const now = toZonedTime(new Date(), timeZone);
  const zonedDate = toZonedTime(date, timeZone);
  return isSameDay(zonedDate, now);
};

/**
 * Checks if a date is yesterday in a specific timezone
 *
 * @param date - The date to check
 * @param timeZone - The timezone to perform the check in. If not provided, uses local timezone
 * @returns True if the date is yesterday in the specified timezone, false otherwise
 */
export const isYesterday = (date: Date, timeZone: string): boolean => {
  const now = toZonedTime(new Date(), timeZone);
  const yesterday = addDays(now, -1);

  const zonedDate = toZonedTime(date, timeZone);

  return isSameDay(zonedDate, yesterday);
};
